import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BlockIcon from '@mui/icons-material/Block';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import BookingConfirmDialog from './BookingConfirmDialog';

/**
 * Reusable action dialog for booking status changes (reject, cancel, no-show).
 * Confirming has its own dialog with an editable time: BookingConfirmDialog.
 */
export function BookingActionDialog({ open, onClose, title, message, confirmLabel, confirmColor, onConfirm, loading }) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography>{message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('Cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={confirmColor || 'primary'}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Booking action buttons for a single booking row.
 * Shows different buttons based on booking status.
 */
export default function BookingActions({ booking, t, onActionComplete }) {
  const [dialogType, setDialogType] = useState(null); // 'confirm' | 'reject' | 'cancel' | 'no_show'
  const [loading, setLoading] = useState(false);

  const handleAction = async () => {
    setLoading(true);
    try {
      await axios.post(`/api/bookings/${booking.id}/${dialogType}/`);
      const messages = {
        reject: t('booking.actions.rejected'),
        cancel: t('booking.actions.cancelled'),
        no_show: t('booking.actions.marked_no_show'),
      };
      onActionComplete(messages[dialogType], 'success');
      setDialogType(null);
    } catch (err) {
      console.error('Booking action failed:', err);
      onActionComplete(err.response?.data?.error || 'Error', 'error');
      setDialogType(null);
    } finally {
      setLoading(false);
    }
  };

  // 'confirm' is not here: it opens BookingConfirmDialog (editable time) below.
  const dialogConfig = {
    reject: {
      title: t('booking.actions.reject_title'),
      message: t('booking.actions.reject_message'),
      confirmLabel: t('booking.actions.reject'),
      confirmColor: 'error',
    },
    cancel: {
      title: t('booking.actions.cancel_title'),
      message: t('booking.actions.cancel_message'),
      confirmLabel: t('booking.actions.cancel'),
      confirmColor: 'error',
    },
    no_show: {
      title: t('booking.actions.no_show_title'),
      message: t('booking.actions.no_show_message'),
      confirmLabel: t('booking.actions.no_show'),
      confirmColor: 'warning',
    },
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 0.5, whiteSpace: 'nowrap' }}>
        {booking.status === 'pending' && (
          <>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<CheckCircleIcon />}
              onClick={() => setDialogType('confirm')}
              sx={{ textTransform: 'none', fontSize: '12px', flex: 1 }}
            >
              {t('booking.actions.confirm')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => setDialogType('reject')}
              sx={{ textTransform: 'none', fontSize: '12px', flex: 1 }}
            >
              {t('booking.actions.reject')}
            </Button>
          </>
        )}
        {booking.status === 'confirmed' && (
          <>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<BlockIcon />}
              onClick={() => setDialogType('cancel')}
              sx={{ textTransform: 'none', fontSize: '12px', flex: 1 }}
            >
              {t('booking.actions.cancel')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<PersonOffIcon />}
              onClick={() => setDialogType('no_show')}
              sx={{ textTransform: 'none', fontSize: '12px', flex: 1 }}
            >
              {t('booking.actions.no_show')}
            </Button>
          </>
        )}
      </Box>

      {dialogType === 'confirm' && (
        <BookingConfirmDialog
          booking={booking}
          open
          onClose={() => setDialogType(null)}
          onActionComplete={onActionComplete}
        />
      )}

      {dialogType && dialogType !== 'confirm' && dialogConfig[dialogType] && (
        <BookingActionDialog
          open={!!dialogType}
          onClose={() => setDialogType(null)}
          title={dialogConfig[dialogType].title}
          message={dialogConfig[dialogType].message}
          confirmLabel={dialogConfig[dialogType].confirmLabel}
          confirmColor={dialogConfig[dialogType].confirmColor}
          onConfirm={handleAction}
          loading={loading}
        />
      )}
    </>
  );
}
